import api from "api";
import * as constants from "./actionTypes";
import { processFormData, sortArrayByName, showToast } from "../utils/helpers";
import cloneDeep from "lodash.clonedeep";
import { navigate } from "@reach/router";
import { getSavedProjectField } from "../utils/getSavedProjectField";
import { getSavedCompanyField } from "utils/getSavedCompanyField";

export const DEFAULT_PAGE_SIZE = 24;

// add request cache to prevent duplicate API calls
const requestCache = new Map();
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

function createCacheKey(endpoint, params = {}) {
	return `${endpoint}?${new URLSearchParams(params).toString()}`;
}

function getCachedRequest(cacheKey) {
	const cached = requestCache.get(cacheKey);
	if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
		return cached.data;
	}
	return null;
}

function setCachedRequest(cacheKey, data) {
	requestCache.set(cacheKey, {
		data,
		timestamp: Date.now(),
	});
}

// to clear cache when needed
export function clearCompaniesCache() {
	requestCache.clear();
}

// TODO: Replace all uses with api-v1 or v2
function getApiWithToken() {
	return api;
}

export const getCompanies = (filtername, sort) => {
	if (!sort) {
		sort = "name";
	}

	return async (dispatch) => {
		// only cache when there's no search filter (filtername is empty)
		// This allows fresh results for search queries while caching the default list
		const shouldCache = !filtername || filtername.trim() === "";

		let cachedData = null;
		if (shouldCache) {
			const cacheKey = createCacheKey("companies", {
				page_size: DEFAULT_PAGE_SIZE,
				"filter[name]": filtername || "",
				sort: sort,
			});

			cachedData = getCachedRequest(cacheKey);
			if (cachedData) {
				dispatch({
					type: constants.GET_COMPANIES,
					payload: cachedData,
				});
				return;
			}
		}

		dispatch({ type: constants.IS_LOADING, payload: true });
		try {
			const companies = await getApiWithToken()
				.get(
					`companies?page_size=${DEFAULT_PAGE_SIZE}&filter[name]=${filtername}&sort=${sort}`
				)
				.json();

			// only cache non-search results (default company listings)
			if (shouldCache) {
				const cacheKey = createCacheKey("companies", {
					page_size: DEFAULT_PAGE_SIZE,
					"filter[name]": filtername || "",
					sort: sort,
				});
				setCachedRequest(cacheKey, companies);
			}

			await dispatch({
				type: constants.GET_COMPANIES,
				payload: companies,
			});
		} catch (error) {
			console.error(error);
		}
		await dispatch({ type: constants.IS_LOADING, payload: false });
	};
};

export const getCompaniesByPage = (page) => async (dispatch) => {
	// cache pagination requests for default sort (name) as they're commonly repeated
	const cacheKey = createCacheKey("companies", {
		page: page,
		page_size: DEFAULT_PAGE_SIZE,
		sort: "name",
	});

	const cachedData = getCachedRequest(cacheKey);
	if (cachedData) {
		dispatch({
			type: constants.GET_COMPANIES,
			payload: cachedData,
		});
		return;
	}

	dispatch({ type: constants.IS_LOADING, payload: true });
	try {
		const companies = await getApiWithToken()
			.get(`companies?page=${page}&page_size=${DEFAULT_PAGE_SIZE}&sort=name`)
			.json();

		// cache this pagination result
		setCachedRequest(cacheKey, companies);

		dispatch({
			type: constants.GET_COMPANIES,
			payload: companies,
		});
	} catch (error) {
		console.error(error);
	}

	dispatch({ type: constants.IS_LOADING, payload: false });
};

export const getCompanyView = (search_company_id) => async (dispatch) => {
	dispatch({ type: constants.IS_LOADING, payload: true });
	try {
		const response = await getApiWithToken()
			.get(
				`companies/${search_company_id}?past_projects_published=true
				}`
			)
			.json();

		if (response?.data) {
			await dispatch({ type: constants.GET_COMPANY, payload: response.data });
			await dispatch({ type: constants.IS_LOADING, payload: false });
			return { status: 200 };
		} else {
			throw new Error("Invalid response format");
		}
	} catch (e) {
		const error = await e.response?.json();
		await dispatch({ type: constants.IS_LOADING, payload: false });
		return error || { status: 500, message: "Failed to fetch company data" };
	}
};

export const getDocumentAccessRequests = (company_id) => {
	return async (dispatch) => {
		(async () => {
			const access_requests = await getApiWithToken()
				.get(`documents/companies/${company_id}/request`)
				.json();
			await dispatch({
				type: constants.DOCUMENT_ACCESS_REQUEST,
				payload: access_requests,
			});
		})();
	};
};

export const getPastProjects =
	(companyId, published = false) =>
		async (dispatch) => {
			dispatch({ type: constants.FETCHING_PAST_PROJECTS, payload: true });

			try {
				const pastProjects = await getApiWithToken()
					.get(
						`past-projects?filter[company_id]=${companyId}${`&published=${!!published}`}`
					)
					.json();
				await dispatch({
					type: constants.GET_PAST_PROJECTS,
					payload: pastProjects.data,
				});
				return pastProjects;
			} catch (e) {
				console.log(e);
			}

			await dispatch({
				type: constants.FETCHING_PAST_PROJECTS,
				payload: false,
			});
		};

export const updateProjectOrderings = async (form) => {
	const formData = processFormData(form);
	const res = await getApiWithToken()
		.patch(`past-projects-order`, { body: formData })
		.json();
	return res;
};

export const addPastProject = (values) => {
	return async (dispatch) => {
		const formData = processFormData(values);
		const company = await getApiWithToken()
			.post(`past-projects`, { body: formData, timeout: 30000 })
			.json();
		await dispatch({ type: constants.ADD_PAST_PROJECT, payload: company.data });
		return company;
	};
};

export const addDraftPastProject = (values) => {
	return async (dispatch) => {
		dispatch({ type: constants.SAVING_DRAFT_PAST_PROJECT, payload: true });
		const formData = processFormData(values);
		const company = await getApiWithToken()
			.post(`past-projects`, { body: formData, timeout: 30000 })
			.json();
		await dispatch({ type: constants.ADD_PAST_PROJECT, payload: company.data });
		await dispatch({
			type: constants.SAVING_DRAFT_PAST_PROJECT,
			payload: false,
		});
		return company;
	};
};

export const editPastProject = (
	projectId,
	values,
	isNotAuto,
	setSavedField,
	tradeValues,
	visibilityEdit
) => {
	return async (dispatch) => {
		const formData = processFormData(values);
		const company = await getApiWithToken()
			.patch(`past-projects/${projectId}`, { body: formData, timeout: 30000 })
			.json();
		if (visibilityEdit) {
			await dispatch({
				type: constants.EDIT_PAST_PROJECT,
				payload: company.data,
			});
		} else if (company && isNotAuto) {
			const toastId = `project_update_${projectId}`;
			showToast("Project updated successfully.", "Success", true, toastId);
		} else {
			const savedValues = getSavedProjectField(company, tradeValues);
			setSavedField(savedValues);
		}
		return company;
	};
};

export const deletePastProject = (projectId) => {
	return async (dispatch) => {
		dispatch({ type: constants.IS_REQUESTING, payload: true });
		const response = await getApiWithToken()
			.delete(`past-projects/${projectId}`)
			.json();
		await dispatch({ type: constants.DELETE_PAST_PROJECT, payload: response });
		await dispatch({ type: constants.IS_REQUESTING, payload: false });
		return response;
	};
};

export const searchPastProjects = (value, companyId = null) => {
	return async (dispatch) => {
		dispatch({ type: constants.IS_SEARCHING_PROJECTS, payload: true });
		const response = await getApiWithToken()
			.get(
				`past-projects?company_id=${companyId}&search=${value}`
			)
			.json();
		await dispatch({
			type: constants.SEARCH,
			payload: { response, searchEntity: "projects" },
		});
		await dispatch({ type: constants.IS_SEARCHING_PROJECTS, payload: false });
		return response;
	};
};

export const searchCompanies = (value) => {
	return async (dispatch) => {
		const companies = await getApiWithToken()
			.get(`companies/search?search=${value}`)
			.json();
		let companiesArray = [];

		Object.keys(companies).forEach((company, index) => {
			companiesArray[index] = { id: company, name: companies[company] };
		});

		await dispatch({
			type: constants.SEARCH,
			payload: {
				response: { data: companiesArray },
				searchEntity: "companies",
			},
		});
	};
};

export const submitCompanyInformationForm = (id, values, setSavedField) => {
	return async (dispatch) => {
		const submitValues = cloneDeep(values);
		if (submitValues.global_presence) {
			submitValues.global_presence = submitValues.global_presence?.map(
				(obj) => obj.id || obj.value
			);
		}
		const formData = processFormData(submitValues);
		const company = await getApiWithToken()
			.patch(`companies/${id}`, { body: formData })
			.json();
		const savedValues = getSavedCompanyField(company.data);
		await dispatch({
			type: constants.SUBMIT_COMPANY_PROFILE_FORM,
			payload: company.data,
		});
		setSavedField(savedValues);
		return company;
	};
};

export const updateCardDetails = (values, setSubmitting, last4) => {
	return async (dispatch) => {
		setSubmitting(true);
		const formData = processFormData(values);
		const result = await getApiWithToken()
			.patch(`auth/users/update-card-details`, { body: formData })
			.json();
		if (result.success) {
			showToast("Details Saved", "Success", true);
			navigate("./?tab=Account Management");
		} else {
			showToast("Update Card Details Failed", "Error");
		}
		setSubmitting(false);
		dispatch({ type: constants.UPDATE_LAST4, payload: last4 });
		return result;
	};
};

// TODO: need to see if we need to reload current company profile after updating subscription
export const updateSubscription = (
	values,
	setSubmitting,
	prevId,
	accountTypes,
	setStatus,
	setIsOpen
) => {
	const planData = {
		plan: accountTypes[values.account_type_id - 1].package_name,
		description: accountTypes[values.account_type_id - 1].description,
	};
	return async (dispatch) => {
		setSubmitting(true);
		setStatus(null);
		try {
			setSubmitting(true);
			const formData = processFormData(values);
			const result = await getApiWithToken()
				.post(`subscription/change`, { body: formData })
				.json();
			setSubmitting(false);
			await dispatch({
				type: constants.UPDATE_SUBSCRIPTION_ACCOUNT,
				payload: values,
			});
			await dispatch({
				type: constants.UPDATE_SUBSCRIPTION_PROFILE,
				payload: values,
			});
			await dispatch({ type: constants.UPDATE_PLAN_DATA, payload: planData });
			if (result.message === "Success to change subscription.") {
				showToast("Success to change subscription.", "Success", true);
				navigate("edit-subscription/success-message", {
					state: { idClicked: prevId },
				});
				return result;
			}
		} catch (e) {
			const error = await e.response.json();
			showToast(error.message, "Error");
			setSubmitting(false);
			setStatus("Stripe");
			setIsOpen(false);
		}
	};
};

export const getResources = (companyId) => async (dispatch) => {
	dispatch({ type: constants.REQUESTING_RESOURCES, payload: true });
	try {
		const resources = await getApiWithToken()
			.get(
				`companies/${companyId}/key-personnel?filter[company_id]=${companyId}`
			)
			.json();
		await dispatch({
			type: constants.GET_RESOURCES,
			payload: resources.data,
		});
	} catch (e) {
		console.log(e);
	}
	await dispatch({ type: constants.REQUESTING_RESOURCES, payload: false });
};

export const addResource = (values) => {
	return async (dispatch) => {
		const formData = processFormData(values);
		try {
			const resource = await getApiWithToken()
				.post("companies/key-personnel/create", {
					body: formData,
				})
				.json();
			await dispatch({ type: constants.ADD_RESOURCE, payload: resource.data });
			return resource;
		} catch (e) {
			const error = await e.response.json();
			showToast(error.message, "Error");
			throw error;
		}
	};
};

export const editResource = (resourceId, values, setErrors, setStatus) => {
	return async (dispatch) => {
		const formData = processFormData(values);
		try {
			const resource = await getApiWithToken()
				.patch(`companies/key-personnel/update/${resourceId}`, {
					body: formData,
				})
				.json();
			await dispatch({ type: constants.EDIT_RESOURCE, payload: resource });
			return resource;
		} catch (e) {
			const error = await e.response.json();
			showToast(error.message, "Error");
			throw error;
		}
	};
};

export const deleteResource = (resourceId) => {
	return async (dispatch) => {
		dispatch({ type: constants.IS_REQUESTING, payload: true });
		try {
			const response = await getApiWithToken()
				.delete(`companies/key-personnel/delete/${resourceId}`)
				.json();
			await dispatch({
				type: constants.DELETE_RESOURCE,
				payload: response,
				id: parseInt(resourceId),
			});
			await dispatch({ type: constants.IS_REQUESTING, payload: false });
			return response;
		} catch (e) {
			const error = await e.response.json();
			showToast(error.message, "Error");
			await dispatch({ type: constants.IS_REQUESTING, payload: false });
			throw error;
		}
	};
};

export const getDocumentCategories = (profileTypeId) => {
	return async (dispatch) => {
		(async () => {
			try {
				const categories = await getApiWithToken()
					.get(
						`document-categories?filter[exclude_from_profile_id]=${profileTypeId}`
					)
					.json();
				await dispatch({
					type: constants.GET_DOCUMENT_CATEGORIES,
					payload: categories.data,
				});
			} catch (e) {
				const error = await e.response.json();
				showToast(error.message, "Error");
			}
		})();
	};
};

export const getDocumentSubCategories = () => {
	return async (dispatch) => {
		(async () => {
			try {
				const subcategories = await getApiWithToken()
					.get("document-subcategories")
					.json();
				let data = sortArrayByName(subcategories?.data);
				await dispatch({
					type: constants.GET_DOCUMENT_SUBCATEGORIES,
					payload: data,
				});
			} catch (e) {
				const error = await e.response.json();
				showToast(error.message, "Error");
			}
		})();
	};
};

export const getDocuments = (companyId) => async (dispatch) => {
	dispatch({ type: constants.IS_REQUESTING, payload: true });

	try {
		const documents = await getApiWithToken()
			.get(
				`documents?filter[documentable_id]=${companyId}&filter[documentable_type]=App\\Models\\Company`
			)
			.json();
		await dispatch({
			type: constants.GET_DOCUMENTS,
			payload: documents.data,
		});
	} catch (e) {
		const error = await e.response.json();
		showToast(error.message, "Error");
	}
	await dispatch({ type: constants.IS_REQUESTING, payload: false });
};

export const addDocument = (companyId, values) => {
	return async (dispatch) => {
		const formData = processFormData(values);
		try {
			const document = await getApiWithToken()
				.post(`documents/companies/${companyId}`, {
					body: formData,
					timeout: false,
				})
				.json();
			await dispatch({ type: constants.ADD_DOCUMENT, payload: document });
			return document;
		} catch (e) {
			const error = await e.response.json();
			showToast(error.message, "Error");
			throw error;
		}
	};
};

export const sendDocumentRequest = (companyId, values) => {
	return async (dispatch) => {
		const formData = processFormData(values);
		try {
			const request = await getApiWithToken()
				.post(`documents/companies/${companyId}/request`, {
					body: formData,
					timeout: false,
				})
				.json();
			await dispatch({
				type: constants.SEND_DOCUMENT_ACCESS_REQUEST,
				payload: request,
			});
		} catch (e) {
			const error = await e.response.json();
			showToast(error.message, "Error");
			throw error;
		}
	};
};

export const updateDocumentRequest = (companyId, values) => {
	return async (dispatch) => {
		const formData = processFormData(values);
		try {
			const request = await getApiWithToken()
				.put(`documents/companies/${companyId}/request`, { body: formData })
				.json();
			await dispatch({
				type: constants.UPDATE_DOCUMENT_ACCESS_REQUEST,
				payload: request.data,
			});
		} catch (e) {
			const error = await e.response.json();
			showToast(error.message, "Error");
			throw error;
		}
	};
};

export const editDocument = (documentId, values) => {
	return async (dispatch) => {
		const formData = processFormData(values);
		try {
			const document = await getApiWithToken()
				.patch(`documents/${documentId}`, { body: formData })
				.json();
			await dispatch({ type: constants.EDIT_DOCUMENT, payload: document });
			return document;
		} catch (e) {
			const error = await e.response.json();
			showToast(error.message, "Error");
			throw error;
		}
	};
};

export const deleteDocument = (documentId) => {
	return async (dispatch) => {
		dispatch({ type: constants.DOCUMENT_IS_DELETING, payload: true });

		try {
			const document = await getApiWithToken()
				.delete(`documents/${documentId}`)
				.json();
			await dispatch({ type: constants.DELETE_DOCUMENT, payload: document });
			dispatch({ type: constants.DOCUMENT_IS_DELETING, payload: false });
		} catch (e) {
			const error = await e.response.json();
			dispatch({ type: constants.DOCUMENT_IS_DELETING, payload: false });
			showToast(error.message, "Error");
		}
	};
};

export const toggleDocumentDisplay = (documentId, values) => {
	return async (dispatch) => {
		try {
			const document = await getApiWithToken()
				.patch(`users/${documentId}`, { json: values })
				.json();
			await dispatch({ type: constants.EDIT_DOCUMENT, payload: document });
			return document;
		} catch (e) {
			const error = await e.response.json();
			showToast(error.message, "Error");
			throw error;
		}
	};
};

export const getExpertiseDisciplines = () => {
	return async (dispatch) => {
		try {
			const methods = await getApiWithToken()
				.get("expertise-disciplines")
				.json();
			let dataObj = {};

			methods &&
				Array.isArray(methods.data) &&
				methods.data.forEach((method) => {
					let disciplineData = method.disciplines.map((discipline) => ({
						value: discipline.id,
						label: discipline.name,
					}));

					dataObj[method.name] = disciplineData;
				});

			await dispatch({
				type: constants.GET_EXPERTISE_DISCIPLINES,
				payload: dataObj,
			});
		} catch (e) {
			const error = await e.response.json();
			showToast(error.message, "Error");
			throw error;
		}
	};
};

export const getDisciplinesByProfileType = (profileTypeId) => {
	return async (dispatch) => {
		try {
			let disciplines = await getApiWithToken()
				.get(`disciplines/by-profile-type/${profileTypeId}`)
				.json();

			let disciplineOptions = Object.keys(disciplines).reduce(
				(accumulator, discipline) => {
					const values = disciplines[discipline];
					accumulator[discipline] = Object.values(values).reduce((acc, d) => {
						if (d.name && d.id) {
							acc.push({ label: d.name, value: d.id });
						}
						return acc;
					}, []);
					return accumulator;
				},
				{}
			);

			await dispatch({
				type: constants.GET_DISCIPLINES_BY_PROFILE_TYPE,
				payload: disciplineOptions,
			});
		} catch (e) {
			const error = await e.response.json();
			showToast(error.message, "Error");
			throw error;
		}
	};
};

export const getCompanySizeOptions = (profileType) => {
	return async (dispatch) => {
		try {
			let sizes = [];
			if (profileType) {
				sizes = await getApiWithToken()
					.get(`companies/sizes/${profileType}`)
					.json();
			}
			await dispatch({
				type: constants.GET_COMPANY_SIZE_OPTIONS,
				payload: sizes,
			});
		} catch (e) {
			const error = await e.response.json();
			showToast(error.message, "Error");
			throw error;
		}
	};
};

export const getLocalGovtAssociations = () => {
	return async (dispatch) => {
		try {
			let lgas = [];
			lgas = await getApiWithToken()
				.get(`companies/local-govt-associations`)
				.json();
			await dispatch({
				type: constants.GET_LOCAL_GOVT_ASSOCIATION,
				payload: lgas,
			});
		} catch (e) {
			const error = await e.response.json();
			showToast(error.message, "Error");
			throw error;
		}
	};
};

export const getTrades = () => {
	return async (dispatch) => {
		try {
			let trades = [];

			trades = await getApiWithToken().get("trades?page_size=9999").json();

			if (trades.data) {
				trades = trades.data.map((trade) => ({
					label: trade.name,
					value: trade.id,
				}));
			}

			await dispatch({ type: constants.GET_TRADES, payload: trades });
		} catch (e) {
			const error = await e.response.json();
			showToast(error.message, "Error");
			throw error;
		}
	};
};

export const getTradeValues = () => {
	return async (dispatch) => {
		try {
			let tradeValues = [];
			tradeValues = await getApiWithToken().get("trade-values").json();
			await dispatch({
				type: constants.GET_TRADES_VALUE,
				payload: tradeValues.data,
			});
		} catch (e) {
			const error = await e.response.json();
			showToast(error.message, "Error");
			throw error;
		}
	};
};

export const getSpecialities = () => {
	return async (dispatch) => {
		try {
			let specialities = [];

			specialities = await getApiWithToken().get("speciality").json();

			if (specialities.data) {
				specialities = specialities.data.map((trade) => ({
					label: trade.name,
					value: trade.id,
				}));
			}
			await dispatch({
				type: constants.GET_SPECIALITIES,
				payload: specialities,
			});
		} catch (e) {
			const error = await e.response.json();
			showToast(error.message, "Error");
			throw error;
		}
	};
};

export const getPostcodeRegions = () => {
	return async (dispatch) => {
		(async () => {
			try {
				const postcodeRegions = await getApiWithToken()
					.get("postcode-regions")
					.json();
				await dispatch({
					type: constants.GET_POSTCODE_REGIONS,
					payload: postcodeRegions,
				});
			} catch (e) {
				const error = await e.response.json();
				showToast(error.message, "Error");
			}
		})();
	};
};

export const getProrateAmount = () => {
	return async (dispatch) => {
		const amount = 0;
		await dispatch({
			type: constants.GET_PRORATE_AMOUNT,
			payload: amount,
		});
	};
};
